import { changeDescription } from 'flow/store/redux/types';
import type {
  ChangeDescription,
  UiEffectContext,
  UiEffectContextByTarget,
  WorkflowDiff,
  WorkflowHistoryEntry,
} from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { generatePublicId } from '../publicIds';
import { ActionType } from 'flow/store/engine/constants';
import { RuleBuilderSubEditorSide } from 'flow/store/engine/types';
import {
  getDatarouteHtmlElementId,
  getMatrixValueHtmlElementId,
  getMLAHtmlElementId,
  getNodeEditorFormHtmlElementId,
} from 'flow/editor/hooks/useWorkflowHistory/helpers';

/**
 * Registry of per-target ui effect context util builders. Add a new entry here (and to `UiEffectContextByTarget`)
 * to support building another kind of UI effect context in tests.
 */
const uiEffectContextBuilders: {
  [T in ChangeDescription]: (
    overrides: Partial<UiEffectContextByTarget[T]>
  ) => UiEffectContextByTarget[T];
} = {
  [changeDescription.action]: overrides => ({
    publicId: generatePublicId('action', ActionType.INPUT),
    ...overrides,
    target: changeDescription.action,
  }),
  [changeDescription.threshold]: overrides => ({
    publicId: generatePublicId('threshold'),
    actionId: generatePublicId('action', ActionType.TAG),
    ...overrides,
    target: changeDescription.threshold,
  }),
  [changeDescription.expression]: overrides => ({
    expressionPath: '[0]',
    actionId: generatePublicId('action', ActionType.OUTPUT),
    ...overrides,
    target: changeDescription.expression,
  }),
  [changeDescription.group]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    ...overrides,
    target: changeDescription.group,
  }),
  [changeDescription.mla]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    subEditorArgs: {
      threshold: {
        publicId: generatePublicId('threshold'),
        side: RuleBuilderSubEditorSide.LHS,
      },
    },
    htmlElementId: getMLAHtmlElementId('filters'),
    ...overrides,
    target: changeDescription.mla,
  }),
  [changeDescription.matrixValue]: overrides => ({
    actionId: generatePublicId('action', ActionType.MATRIX_MODEL),
    htmlElementId: getMatrixValueHtmlElementId({ cellPosition: { row: 1, col: 1 } }),
    ...overrides,
    target: changeDescription.matrixValue,
  }),
  [changeDescription.aggregation]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    subEditorArgs: {
      threshold: {
        publicId: generatePublicId('threshold'),
        side: RuleBuilderSubEditorSide.LHS,
      },
    },
    ...overrides,
    target: changeDescription.aggregation,
  }),
  [changeDescription.inputConfiguration]: overrides => ({
    actionId: generatePublicId('action', ActionType.INPUT),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'input--configuration',
      'attributeName',
    ),
    ...overrides,
    target: changeDescription.inputConfiguration,
  }),
  [changeDescription.serviceLocalSettings]: overrides => ({
    actionId: generatePublicId('action', ActionType.SERVICE),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'service--configuration',
      'displayNameOption.value',
    ),
    ...overrides,
    target: changeDescription.serviceLocalSettings,
  }),
  [changeDescription.dataroute]: overrides => ({
    actionId: generatePublicId('action', ActionType.SERVICE),
    htmlElementId: getDatarouteHtmlElementId(generatePublicId('dataroute')),
    ...overrides,
    target: changeDescription.dataroute,
  }),
  [changeDescription.outputOptions]: overrides => ({
    actionId: generatePublicId('action', ActionType.OUTPUT),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'output--options',
      'attributeName',
    ),
    ...overrides,
    target: changeDescription.outputOptions,
  }),
  [changeDescription.outputJqSettings]: overrides => ({
    actionId: generatePublicId('action', ActionType.OUTPUT),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'output--rule',
      'useJq',
    ),
    ...overrides,
    target: changeDescription.outputJqSettings,
  }),
  [changeDescription.matrixOption]: overrides => ({
    actionId: generatePublicId('action', ActionType.MATRIX_MODEL),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'matrix_model--options',
      'dataType',
    ),
    ...overrides,
    target: changeDescription.matrixOption,
  }),
  [changeDescription.snippet]: overrides => ({
    publicId: generatePublicId('action', ActionType.SNIPPET),
    ...overrides,
    target: changeDescription.snippet,
  }),
  [changeDescription.globalSettings]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    ...overrides,
    target: changeDescription.globalSettings,
  }),
  [changeDescription.tagLocalSettings]: overrides => ({
    actionId: generatePublicId('action', ActionType.TAG),
    htmlElementId: getNodeEditorFormHtmlElementId(
      'tag--options',
      'serviceFailureDefaultOption',
    ),
    ...overrides,
    target: changeDescription.tagLocalSettings,
  }),
};

/**
 * Builds a `UiEffectContext` for tests. The return type narrows to the context that matches
 * the requested `target`, with sensible defaults that can be individually overridden.
 */
export function makeUiEffectContext<T extends ChangeDescription>(
  target: T,
  overrides: Partial<UiEffectContextByTarget[T]> = {},
): UiEffectContextByTarget[T] {
  const build = uiEffectContextBuilders[target];

  return build(overrides);
}

export function makeWorkflowHistoryEntry({
  diff,
  operation,
  uiEffectContext,
}: {
  diff?: WorkflowDiff;
  operation?: Operation;
  uiEffectContext?: UiEffectContext;
} = {}): WorkflowHistoryEntry {
  return {
    diff: diff ?? {
      workflowData: {},
      workflowProps: null,
    },
    operation: operation ?? Operation.Add,
    uiEffectContext: uiEffectContext ?? makeUiEffectContext(changeDescription.action),
  };
}
